import { useRef, useState, useEffect } from 'react'
import { Schemas } from '~/api/gen'
import { useBinary } from '~/hooks/useBinary'
import { ImageSearchItemImage, ImageConfig } from './ImageSearchItemImage'

type ContainerSize = {
  width: number
  height: number
}

type Props = {
  img: HTMLImageElement
  imageSearchKey: Schemas.image_search_key
  containerSize: ContainerSize
  isShow: boolean
  onClick: (searchKey: string) => () => void
}

type ItemPosition = {
  positionTop: number
  positionLeft: number
}

const itemPositions: ItemPosition[] = []

/* eslint-disable complexity */
export const ImageSearchItem: React.FC<Props> = ({
  img,
  imageSearchKey,
  containerSize,
  isShow,
  onClick,
}) => {
  const rect = imageSearchKey.rect
  const config: ImageConfig = {
    rectTop: rect ? rect.top : 0,
    rectLeft: rect ? rect.left : 0,
    rectWidth: rect ? rect.right - rect.left : img.width,
    rectHeight: rect ? rect.bottom - rect.top : img.height,
  }
  const [positionTop, setPositionTop] = useState(0)
  const [positionLeft, setPositionLeft] = useState(0)
  const [isLeftward, , rightward] = useBinary(true)
  const ItemRef = useRef<HTMLLIElement>(null)
  const [ItemWidth, setItemWidth] = useState(0)
  const [isLoaded, loaded, ,] = useBinary(false)

  useEffect(() => {
    // 重なりを防ぐ
    const distance = 28
    let top =
      ((config.rectTop + config.rectHeight / 2) / img.naturalHeight) * 100
    let left =
      ((config.rectLeft + config.rectWidth / 2) / img.naturalWidth) * 100

    // 先に配置されたアイテムの位置を見ていく
    for (const before of itemPositions) {
      const beforeTopPx = (before.positionTop * containerSize.height) / 100
      const beforeLeftPx = (before.positionLeft * containerSize.width) / 100
      let currentTopPx = (top * containerSize.height) / 100
      let currentLeftPx = (left * containerSize.width) / 100
      const topDiff = beforeTopPx - currentTopPx
      const leftDiff = beforeLeftPx - currentLeftPx

      if (
        -distance < topDiff &&
        topDiff < distance &&
        -distance < leftDiff &&
        leftDiff < distance
      ) {
        // 縦横がdistanceの中に入っている場合
        if (Math.abs(topDiff) < Math.abs(leftDiff)) {
          // 縦の方が近い場合、横方向に遠ざける
          currentLeftPx += leftDiff < 0 ? distance : -distance
          left = (currentLeftPx / containerSize.width) * 100
        } else {
          // 横の方が近い場合、縦方向に遠ざける
          currentTopPx += topDiff < 0 ? distance : -distance
          top = (currentTopPx / containerSize.height) * 100
        }
      }
    }
    itemPositions.push({ positionTop: top, positionLeft: left })
    setPositionTop(top)
    setPositionLeft(left)

    // 右向き用に設定
    if (left > 50) rightward()
    if (ItemRef.current) setItemWidth(ItemRef.current.offsetWidth)

    loaded()

    return () => {
      itemPositions.length = 0
    }
  }, [
    config.rectHeight,
    config.rectLeft,
    config.rectTop,
    config.rectWidth,
    containerSize.height,
    containerSize.width,
    img.height,
    img.naturalHeight,
    img.naturalWidth,
    img.width,
    loaded,
    rightward,
  ])

  return (
    <li
      ref={ItemRef}
      className={`absolute -mt-5 ${isShow ? 'visible' : 'invisible'}`}
      style={{
        top: `${positionTop}%`,
        left: `${positionLeft}%`,
        marginLeft: `${isLeftward ? 16 : -16 - ItemWidth}px`,
        maxWidth: `${window.innerWidth / 2 - 20}px`,
      }}
    >
      <button
        type="button"
        onClick={onClick(imageSearchKey.search_key)}
        className="visible absolute top-[6px] z-[1] inline-block p-[6px]"
        style={isLeftward ? { left: '-30px' } : { right: '-30px' }}
      >
        <span
          className={`block size-4 transition-[transform,opacity] duration-300 ease-coordinateTag ${
            !isLoaded ? 'scale-0 opacity-0' : ''
          } ${
            isShow
              ? 'bg-white'
              : 'border-2 border-solid border-white bg-transparent'
          } rounded-full`}
        />
      </button>
      <div
        className={`relative z-[2] flex h-10 rounded-[20px] bg-white transition-[transform,visibility] duration-300 ease-coordinateTag ${
          !isShow && 'scale-[0.2]'
        } ${isLeftward ? 'origin-left' : 'origin-right'}`}
      >
        <ImageSearchItemImage img={img} config={config} />
        <p className="truncate pl-[9px] pr-4 text-[14px] font-bold leading-[40px]">
          {imageSearchKey.category.name}
        </p>
      </div>
    </li>
  )
}
